import React, { useMemo, useRef, useState } from "react";
import {
    Box,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Typography,
    Tooltip,
    ListSubheader,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface Option {
    label: string;
    value: string;
    disabled?: boolean;
}

interface DropDownFilterProps {
    placeholder?: string;
    options: Option[];
    value: string[];
    onChange: (next: string[]) => void;
    maxMenuHeight?: number;
    showSelectAll?: boolean;
    disabled?: boolean;
}

const CustomArrow = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
    // <ArrowDropDownIcon
    //     sx={{
    //         transition: "transform 0.2s",
    //         transform: open ? "rotate(180deg)" : "rotate(0deg)",
    //     }}
    // />
    <span
        onClick={(e) => {
            e.stopPropagation();   // prevent Select from double firing
            onClick();
        }}
        style={{
            display: "inline-block",
            width: "11px",
            height: "7px",
            borderRight: "2px solid #000",  // 👈 grey line (adjust color)
            borderBottom: "2px solid #000",
            transform: open ? "rotate(-135deg)" : "rotate(45deg)", // rotate up/down
            transition: "transform 0.2s ease",
            marginLeft: "20px",
            position: "relative",
            right: "10px",
            cursor: "pointer",
            marginBottom:"4px"

        }}
    />
);

const DropDownFilter: React.FC<DropDownFilterProps> = ({
    placeholder = "Select...",
    options,
    value,
    onChange,
    maxMenuHeight = 350,
    showSelectAll = true,
    disabled,
}) => {
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false); // control open state
    const searchRef = useRef<HTMLInputElement>(null);

    // Filtered list
    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        const q = search.toLowerCase();
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q)
        );
    }, [options, search]);

    // All enabled option values
    const enabledValues = useMemo(
        () => options.filter((o) => !o.disabled).map((o) => o.value),
        [options]
    );

    // Is everything selected?
    const allSelected =
        enabledValues.length > 0 &&
        enabledValues.every((v) => value.includes(v));

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const next = event.target.value as string[];
        onChange(next);
    };

    const renderValue = (selected: string[]) => {
        if (!selected.length) {
            return (
                <Typography
                    sx={{
                        color: "text.secondary",
                        width: "100%",
                        textAlign: "left",
                    }}
                >
                    {placeholder}
                </Typography>
            );
        }
        return options
            .filter((o) => selected.includes(o.value))
            .map((o) => o.label)
            .join(", ");
    };

    return (
        <Tooltip
            title={
                value.length
                    ? options
                        .filter((o) => value.includes(o.value))
                        .map((o) => o.label)
                        .join(", ")
                    : placeholder
            }
            placement="top"
            arrow
        >
            <FormControl fullWidth disabled={disabled}>
                <Select
                    multiple
                    displayEmpty
                    value={value}
                    onChange={handleChange}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    input={
                        <OutlinedInput
                            sx={{
                                height: 44,
                                borderRadius: "10px",
                                fontSize: 14,
                                "& .MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                    paddingY: 0,
                                },
                            }}
                        />
                    }
                    renderValue={renderValue}
                    IconComponent={(props) => <CustomArrow open={open} onClick={() => setOpen(!open)} />}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: maxMenuHeight,
                                width: 320,
                                paddingTop: 0,
                                paddingBottom: 0,
                                borderRadius: 10,
                                overflow: 'overlay',
                            },
                        },

                        disableAutoFocusItem: true,
                        MenuListProps: {
                            style: {
                                paddingTop: 0,
                                paddingBottom: 0,
                                maxHeight: maxMenuHeight,
                                overflowY: 'auto', // optional if you want scroll
                            },
                            onKeyDown: (e: React.KeyboardEvent) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                                    setTimeout(() => searchRef.current?.focus(), 0);
                                }
                            },
                        },
                    }}
                >
                    {/* Search row */}
                    <ListSubheader
                        sx={{
                            bgcolor: "white",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            borderBottom: "1px solid #eee",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                px: 1,
                                py: 0.5,
                                border: "1px solid #ccc",
                                borderRadius: 2,
                                bgcolor: "#fafafa",
                            }}
                        >
                            <input
                                ref={searchRef}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                style={{
                                    flex: 1,
                                    border: "none",
                                    outline: "none",
                                    fontSize: 14,
                                    background: "transparent",
                                    padding: "4px",
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                                }}
                            />
                            {(search || open) && (
                                <Box
                                    component="span"
                                    sx={{
                                        cursor: "pointer",
                                        fontWeight: "bold",
                                        fontSize: 18,
                                        lineHeight: 1,
                                        px: 1,
                                        color: "gray",
                                    }}
                                    onClick={() => {
                                        setSearch("");
                                        setOpen(false); // close dropdown on clear
                                    }}
                                >
                                    ×
                                </Box>
                            )}
                        </Box>
                    </ListSubheader>

                    {/* Select All option */}
                    {showSelectAll && !search && (
                        <MenuItem
                            value="ALL"
                            onClick={(e) => {
                                e.stopPropagation(); // prevent dropdown auto-close
                                if (allSelected) {
                                    onChange([]); // clear all
                                } else {
                                    onChange(enabledValues); // select all enabled
                                }
                            }}
                        >
                            <Checkbox checked={allSelected} />
                            <ListItemText primary="Select All" />
                        </MenuItem>
                    )}

                    {/* Options */}
                    {filtered.length ? (
                        filtered.map((opt) => (
                            <MenuItem
                                key={opt.value}
                                value={opt.value}
                                disabled={opt.disabled}
                                dense
                            >
                                <Checkbox checked={value.includes(opt.value)} />

                                <ListItemText primary={opt.label} />

                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled dense>
                            <ListItemText primary="No results" />
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </Tooltip>
    );
};

export default DropDownFilter;
